import React, { useState, useEffect, useRef } from 'react'
import {
  Typography,
  Button,
  DialogActions,
  DialogContent,
  Chip,
} from '@material-ui/core'
import { ArrowBackRounded, ArrowForwardRounded } from '@material-ui/icons'
import EditableDialogWrapper from './editableDialogWrapper'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import DOMPurify from 'dompurify'

const useStyles = makeStyles({
  carouselWrapper: {
    width: '500px',
    overflowY: 'auto',
    position: 'relative',
  },
  carouselHeader: {
    display: 'flex',
    width: '100%',
  },
  carouselBody: {
    overflowY: 'auto',
    maxHeight: '500px',
  },
  headerSection: {
    display: 'flex',
    flex: 1,
  },
  headerMiddle: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  navigationButton: {
    border: 'none',
    background: 'none',
    marginBottom: 'auto',
  },
  nextButton: {
    marginLeft: 'auto',
  },
  prevButton: {
    marginRight: 'auto',
  },
})

const CarouselDialog = ({
  title,
  open,
  description,
  onConfirm,
  onReset,
  onClose,
  currentIndex,
  carouselOptions,
  fontSize,
  fontFamily,
  onChange,
  currentHtmlText,
  resetButtonVisible = true,
  confirmButtonText,
}) => {
  const [hasEntered, setHasEntered] = useState(false)
  const classes = useStyles()
  const [selectedId, setSelectedId] = useState(0)
  const [editedText, setEditedText] = useState('')
  const contentEditorWrapperRef = useRef()
  const contentEditorRef = useRef()

  const selectedIndex = carouselOptions.findIndex(option => option.id === selectedId)
  const selectedOption = carouselOptions[selectedIndex] || {}

  useEffect(() => {
    const initialIndex = currentIndex
      ? carouselOptions.findIndex(x => x.id === currentIndex)
      : 0
    setSelectedId(carouselOptions[initialIndex]?.id ?? 0)
  }, [currentIndex, carouselOptions])

  useEffect(() => {
    const newText =
      currentHtmlText !== selectedOption.value &&
      stripHtmlTags(currentHtmlText) !== title
        ? currentHtmlText
        : selectedOption.value

    setEditedText(newText)

    if (hasEntered && contentEditorRef.current) {
      const $ = window.jQuery
      const editor = $(contentEditorRef.current).data('kendoEditor')
      if (editor) {
        editor.value(newText)
      }
    }
  }, [selectedId, selectedOption, currentHtmlText, title, hasEntered])

  const onContentEditorChange = () => contentEditorChanged()

  const contentEditorChanged = () => {
    let $ = window.jQuery
    const editor = $(contentEditorRef.current).data('kendoEditor')
    if (editor) {
      const newValue = editor.value()
      setEditedText(newValue)
    }
  }

  useEffect(() => {
    if (hasEntered) {
      let $ = window.jQuery

      if (contentEditorRef.current) {
        $(contentEditorRef.current).kendoEditor({
          stylesheets: [
            `/css/editor/font-family-${fontFamily}.css`,
            `/css/editor/font-size-${fontSize}.css`,
          ],
          tools: [
            'bold',
            'italic',
            'underline',
            'justifyLeft',
            'justifyCenter',
            'justifyRight',
            'justifyFull',
            'insertUnorderedList',
            'insertOrderedList',
            'indent',
            'outdent',
            'insertImage',
            'tableWizard',
            'createTable',
            'addRowAbove',
            'addRowBelow',
            'addColumnLeft',
            'addColumnRight',
            'deleteRow',
            'deleteColumn',
            'mergeCellsHorizontally',
            'mergeCellsVertically',
            'splitCellHorizontally',
            'splitCellVertically',
            'viewHtml',
            'formatting',
            'cleanFormatting',
            'copyFormat',
            'applyFormat',
            'foreColor',
            'backColor',
            'fontName',
            'fontSize',
          ],
          change: onContentEditorChange,
          keyup: onContentEditorChange,
          input: onContentEditorChange,
          select: onContentEditorChange,
          execute: onContentEditorChange,
          paste: onContentEditorChange,
        })

        const editor = $(contentEditorRef.current).data('kendoEditor')
        if (editor) {
          editor.value(editedText)
        }

        if (selectedOption.isEditableByUser) {
          contentEditorWrapperRef.current.style.display = 'block'
        }
        return () => {
          const editorInstance = $(contentEditorRef.current).data('kendoEditor')
          if (editorInstance) {
            editorInstance.destroy()
          }
        }
      } else {
        console.error('contentEditorRef is not defined.')
      }
    }
  }, [hasEntered])

  const handleIndexChange = (event, newIndex) => {
    const newId = carouselOptions[newIndex]?.id
    setSelectedId(newId)
    const currentOption = carouselOptions[newIndex]
    if (typeof onChange === 'function') {
      onChange(event, newId, currentOption?.value);
    }
  }

  const stripHtmlTags = input => {
    if (!input) return ''
    return input.replace(/<\/?[^>]+(>|$)/g, '')
  }

  const onClosePreCallback = () => {
    const currentOptionIndex = carouselOptions.findIndex(x => x.id === currentIndex)

    if (selectedIndex !== currentOptionIndex) {
      setSelectedId(carouselOptions[currentOptionIndex]?.id)
    }
    onClose()
  }

  return (
    <EditableDialogWrapper
      title={title}
      open={open}
      onClose={onClosePreCallback}
      onEntered={() => setHasEntered(true)}>
      <DialogContent dividers>
        {description && (
          <Typography>
            {description}
            <hr />
          </Typography>
        )}
        {selectedOption.description && (
          <Typography gutterBottom>
            {selectedOption.description}
          </Typography>
        )}
        <div className={classes.carouselHeader}>
          <div className={classes.headerSection}>
            {selectedIndex > 0 && (
              <button
                type='button'
                className={`${classes.navigationButton} ${classes.prevButton}`}
                onClick={e => handleIndexChange(e, selectedIndex - 1)}>
                <ArrowBackRounded />
              </button>
            )}
          </div>
          <div className={`${classes.headerSection} ${classes.headerMiddle}`}>
            <span className={classes.selectionCounter}>
              {selectedIndex + 1}/{carouselOptions.length}
            </span>
            {selectedOption.id === currentIndex && (
              <Chip
                className={classes.selectedIndicator}
                variant='outlined'
                size='small'
                label='Selected'
                color='primary'
              />
            )}
          </div>
          <div className={classes.headerSection}>
            {selectedIndex < carouselOptions.length - 1 && (
              <button
                type='button'
                className={`${classes.navigationButton} ${classes.nextButton}`}
                onClick={e => handleIndexChange(e, selectedIndex + 1)}>
                <ArrowForwardRounded />
              </button>
            )}
          </div>
        </div>
        <div
          className={classes.carouselBody}
          style={{ fontSize: fontSize, fontFamily: fontFamily }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(selectedOption.value),
          }}></div>
        {selectedOption.isEditableByUser && (
          <div style={{ height: 400 }}>
            <div ref={contentEditorWrapperRef} style={{ display: 'none' }}>
              <textarea style={{ height: 380 }} ref={contentEditorRef} />
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {resetButtonVisible && (
          <Button onClick={() => onReset()} color='secondary'>
            Reset
          </Button>
        )}
        <Button
          variant='contained'
          onClick={() => onConfirm(selectedId, editedText)}
          color='secondary'>
          {confirmButtonText || 'Confirm'}
        </Button>
      </DialogActions>
    </EditableDialogWrapper>
  )
}

CarouselDialog.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  currentIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  carouselOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      isEditableByUser: PropTypes.bool,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
  fontSize: PropTypes.string,
  fontFamily: PropTypes.string,
  onChange: PropTypes.func.isRequired, 
  currentHtmlText: PropTypes.string.isRequired,
  resetButtonVisible: PropTypes.bool,
  confirmButtonText: PropTypes.string,
}

export default CarouselDialog
